class SideScroll extends HTMLElement {
  /** @type {number} */
  #startX = 0;
  /** @type {number} */
  #scrollLeft = 0;
  /** @type {boolean} */
  #isDragging = false;
  /** @type {string} */
  #draggingClass = 'cursor-grabbing';
  /** @type {number} */
  #scrollSpeed = parseFloat(this.getAttribute('data-scroll-speed')) || 1;

  /** @type {Array<[HTMLElement, string, (event: MouseEvent) => void]>} */
  #events = [
    [this, 'mousedown', this.#handleMouseDown.bind(this)],
    [this, 'mouseup', this.#handleMouseUp.bind(this)],
    [this, 'mousemove', this.#handleMouseMove.bind(this)],
    [this, 'mouseleave', this.#handleMouseUp.bind(this)],
    [window, 'resize', this.#checkOverflow.bind(this)],
  ];

  connectedCallback() {
    this.#checkOverflow();
    for (const [target, event, handler] of this.#events) {
      target.addEventListener(event, handler);
    }
  }

  disconnectedCallback() {
    for (const [target, event, handler] of this.#events) {
      target.removeEventListener(event, handler);
    }
  }

  /** Handles mouse down events.
   * @description Adds the dragging class and cursor pointer class.
   * @param {MouseEvent} event */
  #handleMouseDown(event) {
    if (!this.#checkOverflow()) return;

    this.#isDragging = true;
    this.classList.add(this.#draggingClass);
    this.#startX = event.pageX - this.offsetLeft;
    this.#scrollLeft = this.scrollLeft;
  }

  /** Handles mouse up events.
   * @description Removes the dragging class and cursor pointer class. */
  #handleMouseUp() {
    if (!this.#checkOverflow() || !this.#isDragging) return;

    this.#isDragging = false;
    this.classList.remove(this.#draggingClass);
  }

  /** Handles mouse move events.
   * @description Scrolls the side scroll horizontally when the mouse is moved while dragging.
   * @param {MouseEvent} event */
  #handleMouseMove(event) {
    if (!this.#isDragging) return;
    event.preventDefault();

    const x = event.pageX - this.offsetLeft;
    const walk = (x - this.#startX) * this.#scrollSpeed;
    this.scrollLeft = this.#scrollLeft - walk;
  }

  /** Checks if the side scroll has overflow and adds the cursor-pointer class if it does.
   * @returns {boolean} Whether the side scroll has overflow.
   */
  #checkOverflow() {
    const overflow = this.scrollWidth > this.clientWidth;
    this.classList.toggle('cursor-pointer', overflow);
    return overflow;
  }
}

if (!customElements.get('side-scroll')) {
  customElements.define('side-scroll', SideScroll);
}
